import {Injectable} from '@angular/core';
import {ApiDataService, selectActiveVehicle} from '@ox/data';
import {GfasPayload, RecallPayload, WarrantyPayload} from '@ox/typings';
import {map, Subject, take, tap, BehaviorSubject, catchError, throwError} from 'rxjs';
import {OX_KEYS,apiUrlMap} from '@ox/config';
import {Store} from '@ngrx/store';

enum gfasType {
  ZPSR, //Product safety recall
  ZNCR, //Non Compliance
  ZPER, //Emission
  ZCSP, //Customer Satisfaction
  ZSPP //Special coverage warranties
}

@Injectable()
export class RecallsGfasService {
  public cmsRecallPrefix = 'recall_classification_';
  public searchedVinEmitter = new Subject<string>();
  private activeVehicle$ = this.store.select(selectActiveVehicle);
  private _vehicleMake = new BehaviorSubject<string>('');
  public vehicleMake$ = this._vehicleMake.asObservable();

  constructor(
    private apiData: ApiDataService,
    private store: Store
  ) {}

  /**
   * TODO:  add unit tests
   * TODO:  add session storage check for specific vin for case where same vin is added to url
   *        temp solution is to force call to account for cases where different vin is searched for
   *        and call isn't being made because it is in session
   * */
  public searchForRecalls(vin: string) {
    const route = apiUrlMap.recallsGfas.get(vin);
    return this.apiData
      .get<GfasPayload>(route, undefined, true)
      .pipe(
        map((response) => {
          const {data} = response;
          const recallResults: RecallPayload[] = [];
          const completeRecallResults: RecallPayload[] = [];
          const specialWarranties: RecallPayload[] = [];

          if (data?.gfas) {
            for (const recallItem of data.gfas) {
              if (recallItem?.type === 'ZSPP') {
                specialWarranties.push(recallItem);
              }
              recallItem.vinStatusInfo?.vinStatus === 'complete'
                ? completeRecallResults.push(recallItem)
                : recallResults.push(recallItem);
            }
            return {
              ...response,
              data: {
                ...response.data,
                recallResults: [...this.setupTypes(recallResults)],
                completeRecallResults: [...this.setupTypes(completeRecallResults)],
                specialWarranties: [...this.setupSpecialWarranties(specialWarranties)],
                number_of_recalls: recallResults.length
              }
            };
          }
          return response;
        }),
        tap(response => {
          this.activeVehicle$.pipe(
            tap(av => {
              if (!response.data) this._vehicleMake.next('');
              if (!av) this._vehicleMake.next(response.data?.make);
              if (av?.vin === vin) this._vehicleMake.next(av.make);
              else this._vehicleMake.next(response.data?.make);
            }),
            take(1)
          ).subscribe();
        }),
        take(1)
      );
  }

  public searchForWarranties(vin: string, profileId?: string | undefined) {
    const route = !profileId ? apiUrlMap.warranties.get(vin) : apiUrlMap.warranties.getWarranties(profileId, vin);

    return this.apiData.get<WarrantyPayload[]>(route, undefined, true).pipe(
      take(1),
      map(
        (response) => {
          const arr = response.data;
          const reducer: { expired: WarrantyPayload[], active: WarrantyPayload[] } = {expired: [], active: []};

          const {expired, active} = arr.reduce(
            (accumulator, current) => {
              const isActive = current?.[OX_KEYS.TAB_DETAIL_ITEM.STATUS] == OX_KEYS.TAB_DETAIL_ITEM.ACTIVE;
              const isExpired = current?.[OX_KEYS.TAB_DETAIL_ITEM.STATUS] == OX_KEYS.TAB_DETAIL_ITEM.EXPIRED;
              const endDate = new Date(current?.[OX_KEYS.TAB_DETAIL_ITEM.END_DATE]);
              //depending on the format of the date look at https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#return_value
              //could have a possible issue in the future if the date string is returned differently could break logic
              const currentDate = new Date();
              const isInDateRange = currentDate < endDate;
              //const isInMileageRange = parseInt(current?.[OX_KEYS.TAB_DETAIL_ITEM.START_MILEAGE]) < parseInt(current?.[OX_KEYS.TAB_DETAIL_ITEM.END_MILEAGE]);
              //I think this is an issue.... since I do not know the current mileage of the car at any given time. Since this can be

              if (isActive && isInDateRange) { // && isInMileageRange
                accumulator.active.push(current);
              } else if (isExpired) {
                accumulator.expired.push(current);
              } else if (isInDateRange) { //isInMileageRange &&
                accumulator.active.push(current);
              } else {
                accumulator.expired.push(current);
              }

              return accumulator;
            },
            reducer);
          return {expired, active};
        }
      ),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  private setupTypes(recallResults: RecallPayload[]) {
    const tempArrayWithCategories: RecallPayload[][] = [[], [], [], [], [], []];
    const temp: RecallPayload[] = [];
    Object.entries(recallResults).map((recallItem: [string, RecallPayload]) => {
      const gfasItem = recallItem[1];
      const {type} = gfasItem;
      gfasItem.classification = this.cmsRecallPrefix + type;
      if (type === gfasType[gfasType.ZPSR]) {
        tempArrayWithCategories[gfasType.ZPSR].push(gfasItem);
      } else if (type === gfasType[gfasType.ZNCR]) {
        tempArrayWithCategories[gfasType.ZNCR].push(gfasItem);
      } else if (type === gfasType[gfasType.ZPER]) {
        tempArrayWithCategories[gfasType.ZPER].push(gfasItem);
      } else if (type === gfasType[gfasType.ZCSP]) {
        tempArrayWithCategories[gfasType.ZCSP].push(gfasItem);
      }
    });
    return [
      ...tempArrayWithCategories[gfasType.ZPSR],
      ...tempArrayWithCategories[gfasType.ZNCR],
      ...tempArrayWithCategories[gfasType.ZPER],
      ...tempArrayWithCategories[gfasType.ZCSP]
    ];
  }

  private setupSpecialWarranties(recallResults: RecallPayload[]) {
    const tempArrayWithCategories: RecallPayload[][] = [[], [], [], [], [], []];
    const temp: RecallPayload[] = [];
    Object.entries(recallResults).map((recallItem: [string, RecallPayload]) => {
      const gfasItem = recallItem[1];
      const {type} = gfasItem;
      gfasItem.classification = this.cmsRecallPrefix + type;
      if (type === gfasType[gfasType.ZSPP]) {
        tempArrayWithCategories[gfasType.ZSPP].push(gfasItem);
      }
    });
    return [
      ...tempArrayWithCategories[gfasType.ZSPP]
    ];
  }
}
